import React from 'react'
import {Box, BoxProps, HStack, Link, Stack, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {Icon} from '_uiComponents'
import {usePageDesigner} from '_customHooks'
import type {Page, SearchHelp, SearchContact, LinkObject} from '_types'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {createRedirectUrl} from '_utils'
import {useGlobalConfigState} from '_context'

/**
 * @name HelpBox
 *
 * @component
 *
 * @description
 * HelpBox component renders a grey box containing a list of informations for client support.
 * It is rendered inside empty search results page.
 *
 * @remarks
 * - `WhatsApp` and `Call us` are partially retrieved via page designer; href is set via custom preference;
 * - `Mail` is completely retrieved via page designer.
 *
 * @returns {React.FC} - Rendered `HelpBox` component.
 */
const HelpBox: React.FC = () => {
    const styles = useMultiStyleConfig('HelpBox') as Record<string, BoxProps>
    const {pageComponents, linkObject} = usePageDesigner({pageId: 'search-help'})
    const data = mapHelpBoxPageDesignerData(linkObject, pageComponents)

    const {locale} = useMultiSite()
    const {appPreferences} = useGlobalConfigState()
    const {customNumberContact, whatsAppEnabledCountries} = appPreferences

    /**
     * Builds hrefs for whatsapp and phone items.
     */
    const buildItemsHref = (): {whatsappHref?: string; phoneHref?: string} => {
        const whatsappData = whatsAppEnabledCountries ? JSON.parse(whatsAppEnabledCountries) : undefined
        const phoneData = customNumberContact ? JSON.parse(customNumberContact) : undefined

        const localizedWhatsappData = whatsappData?.[locale.id.split('-')[1].toLowerCase()]
        const localizedPhoneData = phoneData?.[locale.id.split('-')[1]]?.value

        const whatsappHref = whatsappData && localizedWhatsappData ? `https://wa.me/${localizedWhatsappData}` : undefined
        const phoneHref = phoneData && localizedPhoneData ? `tel:${localizedPhoneData}` : undefined

        return {whatsappHref, phoneHref}
    }

    const {whatsappHref, phoneHref} = buildItemsHref()

    if (!data) {
        console.warn('Help Box page designer data has not been configured.')
        return <></>
    }

    return (
        <Box data-testid="help-box" {...styles.helpBox}>
            {data.title && <Text {...styles.title}>{data.title}</Text>}

            {data.subtitle && <Text {...styles.subtitle}>{data.subtitle}</Text>}

            {data.description && <Text {...styles.description}>{data.description}</Text>}

            <Stack {...styles.buttons}>
                {data.whatsapp && whatsappHref && (
                    <HStack {...styles.button} as={Link} href={whatsappHref} isExternal={data.whatsapp.isExternal}>
                        <Icon name="IconWhatsapp" size="lg" />
                        <Text {...styles.buttonText}>{data.whatsapp.name}</Text>
                    </HStack>
                )}
                {data.phone && phoneHref && (
                    <HStack {...styles.button} as={Link} href={phoneHref} isExternal={data.phone.isExternal}>
                        <Icon name="IconContact" size="lg" />
                        <Text {...styles.buttonText}>{data.phone.name}</Text>
                    </HStack>
                )}
                {data.mail && (
                    <HStack {...styles.button} as={Link} href={data.mail.href} isExternal={data.mail.isExternal}>
                        <Icon name="IconEmail" size="lg" />
                        <Text {...styles.buttonText}>{data.mail.name}</Text>
                    </HStack>
                )}
            </Stack>
        </Box>
    )
}

/**
 * Maps the page designer data of help box.
 *
 * @param {LinkObject} linkObject
 * @param {Page} [pageComponents] - The components of the page.
 * @returns {SearchHelp | undefined} - Mapped search help box data.
 */
const mapHelpBoxPageDesignerData = (linkObject: LinkObject, pageComponents?: Page): SearchHelp | undefined => {
    const components = pageComponents?.regions?.[0]?.components?.[0]?.regions?.[0]?.components

    if (components) {
        const title = components[0]?.data?.text
        const subtitle = components[1]?.data?.text
        const description = components[2]?.data?.text

        const whatsapp: SearchContact | undefined = components[3]?.data?.title
            ? {
                  name: components[3]?.data?.title,
                  href: components[3]?.data?.link,
                  isExternal: components[3]?.data?.target === '_blank',
              }
            : undefined

        const phone: SearchContact | undefined = components[4]?.data?.title
            ? {
                  name: components[4]?.data?.title,
                  href: components[4]?.data?.link,
                  isExternal: components[4]?.data?.target === '_blank',
              }
            : undefined

        const mail: SearchContact | undefined = components[5]?.data?.title
            ? {
                  name: components[5]?.data?.title,
                  href: createRedirectUrl(components[5]?.data?.link, linkObject.hostlink, linkObject.locale, linkObject.site),
                  isExternal: components[5]?.data?.target === '_blank',
              }
            : undefined

        return {
            title,
            subtitle,
            description,
            whatsapp,
            phone,
            mail,
        }
    }

    return undefined
}

export default HelpBox
